import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleMaps } from './GoogleMapsProvider';
import { Save, Edit3, X } from 'lucide-react';

const PropertyMapEditor = ({ 
  coordinates = [], 
  address = "", 
  onSave, 
  propertyArea = 0 
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [polygon, setPolygon] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentArea, setCurrentArea] = useState(propertyArea);
  const [hasChanges, setHasChanges] = useState(false);
  const [geocodedAddress, setGeocodedAddress] = useState(null);
  const [geocodedCoords, setGeocodedCoords] = useState(null);

  const calculateArea = useCallback((coords) => {
    if (!window.google || !coords || coords.length < 3) return 0;
    
    const paths = coords.map(coord => 
      new window.google.maps.LatLng(coord.lat, coord.lng)
    );
    
    const areaMeters = window.google.maps.geometry.spherical.computeArea(paths);
    const areaFeet = areaMeters * 10.7639; // Convert to square feet
    return Math.round(areaFeet);
  }, []);

  useEffect(() => {
    if (!isLoaded || !map) return;

    console.log('PropertyMapEditor - coordinates:', coordinates, 'address:', address);

    // Initialize or update polygon
    if (coordinates && coordinates.length > 0) {
      if (polygon) {
        polygon.setMap(null);
      }

      const newPolygon = new window.google.maps.Polygon({
        paths: coordinates.map(c => ({ lat: c.lat, lng: c.lng })),
        fillColor: '#01500A',
        fillOpacity: 0.35,
        strokeColor: '#ECFF25',
        strokeWeight: 3,
        editable: isEditing,
        draggable: isEditing
      });

      newPolygon.setMap(map);
      setPolygon(newPolygon);

      // Fit map to polygon bounds
      const bounds = new window.google.maps.LatLngBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      map.fitBounds(bounds);
      
      // Zoom out slightly to see the full property
      setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom > 19) {
          map.setZoom(19);
        }
      }, 100);

      // Add listeners for editing
      if (isEditing) {
        const updateArea = () => {
          const path = newPolygon.getPath();
          const coords = [];
          for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            coords.push({ lat: point.lat(), lng: point.lng() });
          }
          const area = calculateArea(coords);
          setCurrentArea(area);
          setHasChanges(true);
        };

        window.google.maps.event.addListener(newPolygon.getPath(), 'set_at', updateArea);
        window.google.maps.event.addListener(newPolygon.getPath(), 'insert_at', updateArea);
        window.google.maps.event.addListener(newPolygon.getPath(), 'remove_at', updateArea);
      }
    } else if (address && address.trim()) {
      // Geocode address if no coordinates
      console.log('🗺️  Géocodage de l\'adresse:', address);
      setGeocodedAddress(address);  // Afficher l'adresse utilisée
      
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address + ', Canada' }, (results, status) => {
        console.log('Résultat géocodage:', status);
        
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          console.log('✅ Coordonnées trouvées:', lat, lng);
          console.log('📍 Adresse formatée:', results[0].formatted_address);
          setGeocodedCoords({ lat, lng });  // Sauvegarder les coordonnées
          
          map.setCenter(location);
          map.setZoom(19);
          
          // Ajouter un marqueur temporaire
          new window.google.maps.Marker({
            position: location,
            map: map,
            title: 'Adresse du client',
            icon: {
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
            }
          });
          
        } else {
          console.error('❌ Géocodage échoué:', status);
          
          if (status === 'REQUEST_DENIED') {
            alert(`🚨 ERREUR API GÉOCODAGE\n\nL'API Geocoding n'est pas activée dans Google Cloud Console.\n\nÉtapes pour corriger:\n1. Aller sur console.cloud.google.com\n2. APIs & Services > Library\n3. Chercher "Geocoding API"\n4. Cliquer ENABLE\n5. Actualiser cette page`);
          } else {
            alert(`⚠️ Impossible de localiser l'adresse:\n"${address}"\n\nErreur: ${status}\n\nVeuillez:\n- Vérifier l'adresse du client\n- Ou dessiner le terrain manuellement`);
          }
          
          setGeocodedCoords(null);
        }
      });
    }

    return () => {
      if (polygon) {
        window.google.maps.event.clearInstanceListeners(polygon.getPath());
      }
    };
  }, [isLoaded, map, coordinates, address, isEditing, calculateArea]);

  useEffect(() => {
    if (!isLoaded || !map || !isEditing) return;

    // Initialize Drawing Manager for new polygon drawing
    if (!drawingManager) {
      const dm = new window.google.maps.drawing.DrawingManager({
        drawingMode: polygon ? null : window.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [window.google.maps.drawing.OverlayType.POLYGON]
        },
        polygonOptions: {
          fillColor: '#01500A',
          fillOpacity: 0.35,
          strokeColor: '#ECFF25',
          strokeWeight: 3,
          editable: true,
          draggable: true
        }
      });

      dm.setMap(map);
      setDrawingManager(dm);

      // Handle polygon complete
      window.google.maps.event.addListener(dm, 'polygoncomplete', (newPolygon) => {
        // Remove old polygon
        if (polygon) {
          polygon.setMap(null);
        }

        setPolygon(newPolygon);
        dm.setDrawingMode(null);

        const path = newPolygon.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coords.push({ lat: point.lat(), lng: point.lng() });
        }
        
        const area = calculateArea(coords);
        setCurrentArea(area);
        setHasChanges(true);

        // Add update listeners
        const updateArea = () => {
          const updatedCoords = [];
          for (let i = 0; i < path.getLength(); i++) {
            const point = path.getAt(i);
            updatedCoords.push({ lat: point.lat(), lng: point.lng() });
          }
          setCurrentArea(calculateArea(updatedCoords));
          setHasChanges(true);
        };

        window.google.maps.event.addListener(path, 'set_at', updateArea);
        window.google.maps.event.addListener(path, 'insert_at', updateArea);
        window.google.maps.event.addListener(path, 'remove_at', updateArea);
      });
    }

    return () => {
      if (drawingManager) {
        drawingManager.setMap(null);
      }
    };
  }, [isLoaded, map, isEditing, calculateArea]);

  const handleSave = () => {
    if (!polygon) {
      alert('Veuillez dessiner un polygone sur la carte.');
      return;
    }

    const path = polygon.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push({ lat: point.lat(), lng: point.lng() });
    }

    onSave({
      coordinates: coords,
      area: currentArea
    });

    setIsEditing(false);
    setHasChanges(false);

    // Make polygon non-editable
    polygon.setEditable(false);
    polygon.setDraggable(false);
    
    if (drawingManager) {
      drawingManager.setMap(null);
      setDrawingManager(null);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHasChanges(false);
    setCurrentArea(propertyArea);

    // Reset polygon to original coordinates
    if (polygon) {
      polygon.setEditable(false);
      polygon.setDraggable(false);
      polygon.setPath(coordinates.map(c => ({ lat: c.lat, lng: c.lng })));
    }

    if (drawingManager) {
      drawingManager.setMap(null);
      setDrawingManager(null);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    if (polygon) {
      polygon.setEditable(true);
      polygon.setDraggable(true);
    }
  };

  if (loadError) {
    return <div className="map-error">Erreur de chargement de Google Maps</div>;
  }

  if (!isLoaded) {
    return <div className="map-loading">Chargement de la carte...</div>;
  }

  return (
    <div className="property-map-editor">
      <div className="map-header">
        <div className="map-info">
          <h3>🗺️ Carte de la propriété</h3>
          <p className="map-area">
            Superficie: <strong>{currentArea.toLocaleString()} pi²</strong>
            {hasChanges && <span className="changes-indicator"> (modifié)</span>}
          </p>
        </div>
        <div className="map-actions">
          {!isEditing ? (
            <>
              <button className="btn-edit-map" onClick={handleEdit}>
                <Edit3 size={16} />
                Modifier
              </button>
              <button 
                className="btn-refresh-map"
                onClick={() => window.location.reload()}
                title="Actualiser la carte"
                style={{ 
                  marginLeft: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                🔄 Actualiser
              </button>
            </>
          ) : (
            <>
              <button className="btn-cancel-map" onClick={handleCancel}>
                <X size={16} />
                Annuler
              </button>
              <button 
                className="btn-save-map" 
                onClick={handleSave}
                disabled={!hasChanges}
              >
                <Save size={16} />
                Enregistrer
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Debug info - Adresse géocodée et coordonnées */}
      {(geocodedAddress || geocodedCoords) && (
        <div style={{
          padding: '0.75rem',
          background: '#FFF9C4',
          border: '1px solid #F57F17',
          borderRadius: '6px',
          marginBottom: '0.5rem',
          fontSize: '0.85rem'
        }}>
          <strong>🔍 Debug Géocodage:</strong>
          {geocodedAddress && <div>📍 Adresse: {geocodedAddress}</div>}
          {geocodedCoords && (
            <div>
              🌍 Coordonnées: {geocodedCoords.lat.toFixed(6)}, {geocodedCoords.lng.toFixed(6)}
            </div>
          )}
        </div>
      )}
      
      <div 
        ref={(el) => {
          if (el && !map) {
            const newMap = new window.google.maps.Map(el, {
              zoom: 19,
              center: coordinates.length > 0 
                ? coordinates[0] 
                : { lat: 45.4042, lng: -71.8929 }, // Sherbrooke default
              mapTypeId: 'hybrid',
              mapTypeControl: true,
              streetViewControl: false,
              fullscreenControl: true
            });
            setMap(newMap);
          }
        }}
        className="map-container"
        style={{ width: '100%', height: '350px', borderRadius: '8px' }}
      />
      
      {isEditing && (
        <div className="map-instructions">
          <p>
            {coordinates.length > 0 
              ? "✏️ Cliquez et déplacez les points du polygone pour ajuster la zone, ou dessinez un nouveau polygone."
              : "✏️ Utilisez l'outil de dessin pour tracer le contour de la propriété sur la carte."}
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyMapEditor;
